/**
 * Admin Fitness — unified dashboard for the fitness/wellness side of the clinic.
 *
 * Tabs: Dashboard · Clients · Trainers · Classes · Schedule · Memberships · Invoices · Analytics.
 *
 * Gated by `settings.feature_fitness`. If the toggle is OFF, this page shows a
 * call-to-action explaining how to turn it on and what it enables.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Dumbbell, Users, UserCircle, Calendar, Wallet, BookOpen, BarChart3, Plus, X, Trash2, RefreshCw, ShieldAlert, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { safeConfirm } from '../../lib/confirm';

const TABS = [
  { id: 'dashboard', label: 'Dashboard',  icon: BarChart3 },
  { id: 'clients',   label: 'Clients',    icon: Users },
  { id: 'trainers',  label: 'Trainers',   icon: UserCircle },
  { id: 'classes',   label: 'Classes',    icon: BookOpen },
  { id: 'schedule',  label: 'Schedule',   icon: Calendar },
  { id: 'memberships', label: 'Memberships', icon: Dumbbell },
  { id: 'invoices',  label: 'Invoices',   icon: Wallet },
];

export default function AdminFitness() {
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    api.get('/clinic/settings').then(r => setSettings(r.data));
  }, []);

  if (!settings) return <div className="p-8">Loading…</div>;
  if (!settings.feature_fitness) return <FitnessDisabled/>;

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">WELLNESS · NON-CLINICAL</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight inline-flex items-center gap-3">
            <Dumbbell className="w-8 h-8 text-brand"/> Fitness & wellness
          </h1>
          <p className="text-fg-muted mt-2 max-w-2xl">
            <strong className="text-fg">Strict separation from clinical care.</strong> Fitness clients are NOT
            patients — they sign a separate <em>waiver of liability</em>, are billed on their own invoice stream,
            and never appear in insurance claims for physiotherapy.
          </p>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-soft overflow-x-auto" data-testid="fitness-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={()=>setTab(t.id)}
                    className={`px-3 py-2 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition whitespace-nowrap ${active ? 'border-brand text-brand font-medium' : 'border-transparent text-fg-muted hover:text-fg'}`}
                    data-testid={`fitness-tab-${t.id}`}>
              <Icon className="w-4 h-4"/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'dashboard'   && <Dashboard/>}
      {tab === 'clients'     && <ClientsTab/>}
      {tab === 'trainers'    && <TrainersTab/>}
      {tab === 'classes'     && <ClassesTab/>}
      {tab === 'schedule'    && <ScheduleTab/>}
      {tab === 'memberships' && <MembershipsTab/>}
      {tab === 'invoices'    && <InvoicesTab/>}
    </div>
  );
}

function FitnessDisabled() {
  // Iter 131 — Fitness is now a paid add-on. Instead of pointing at the
  // (now read-only) settings toggle, we route the clinic owner directly
  // to the add-on purchase page where they can pick "bundled" or
  // "standalone" and either checkout via Stripe or file a manual
  // activation request.
  return (
    <div className="p-8 max-w-3xl mx-auto text-center">
      <Dumbbell className="w-16 h-16 mx-auto text-fg-muted/40 mb-4"/>
      <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PAID ADD-ON</div>
      <h1 className="text-3xl font-heading font-bold mb-2">Fitness &amp; wellness is a paid add-on</h1>
      <p className="text-fg-muted mb-6">
        Fitness is kept strictly separate from clinical care (own client model, own
        waiver, own invoicing stream) which is why it&apos;s billed separately from
        your clinic subscription. Pick <strong>bundled</strong> (added to your
        existing invoice) or <strong>standalone</strong> (independent renewal).
      </p>
      <Link
        to="/admin/fitness-addon"
        className="btn-primary inline-flex items-center gap-2 text-sm"
        data-testid="fitness-goto-addon"
      >
        <ArrowRight className="w-4 h-4"/> See pricing &amp; activate
      </Link>
      <div className="surface border border-soft rounded-lg p-6 text-left text-sm text-fg-secondary mt-8">
        <p className="mb-2"><strong>Once activated, you&apos;ll get:</strong></p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Separate <strong>fitness client</strong> profile + signed waiver of liability.</li>
          <li><strong>Trainer roster</strong> with expertise tags + individual/group session pricing.</li>
          <li><strong>Class calendar</strong> with tier-1/2/3 pricing and membership inclusion.</li>
          <li><strong>Memberships</strong> with patient self-hold + self-renewal.</li>
          <li><strong>Fitness invoices</strong> as a separate stream — never billed as physiotherapy.</li>
          <li>Optional integration with the <code>wrkout</code> and <code>exercises.json</code> open-source datasets for exercise prescription.</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────
function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/fitness/analytics').then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="text-fg-muted text-sm">Loading…</div>;
  return (
    <div className="grid md:grid-cols-4 gap-3 mb-6">
      <Stat label="Total clients" value={stats.total_clients}/>
      <Stat label="Active memberships" value={stats.active_memberships}/>
      <Stat label="Classes defined" value={stats.total_classes}/>
      <Stat label="Upcoming classes" value={stats.upcoming_instances}/>
      <div className="md:col-span-2 bento-card">
        <h3 className="font-heading font-semibold mb-2 text-sm">Revenue (paid)</h3>
        {stats.revenue_paid.length === 0 ? (
          <p className="text-fg-muted text-sm">No paid fitness invoices yet.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {stats.revenue_paid.map((r, i) => (
              <li key={i} className="flex items-center justify-between">
                <span>{r.currency}</span>
                <span className="font-mono font-medium">{r.total.toFixed(2)} ({r.count} invoices)</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="md:col-span-2 bento-card">
        <h3 className="font-heading font-semibold mb-2 text-sm">Pending revenue</h3>
        {stats.revenue_pending.length === 0 ? (
          <p className="text-fg-muted text-sm">No pending invoices.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {stats.revenue_pending.map((r, i) => (
              <li key={i} className="flex items-center justify-between">
                <span>{r.currency}</span>
                <span className="font-mono font-medium text-[#FFB000]">{r.total.toFixed(2)} ({r.count} invoices)</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bento-card">
      <div className="text-xs uppercase tracking-wider text-fg-muted">{label}</div>
      <div className="text-3xl font-heading font-bold text-brand mt-1">{value}</div>
    </div>
  );
}

// ─── Clients ────────────────────────────────────────────────────────────
function ClientsTab() {
  const [rows, setRows] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', goals: '' });
  const load = () => api.get('/fitness/clients').then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post('/fitness/clients', form); setShowCreate(false); setForm({ full_name:'', email:'', phone:'', goals:'' }); load(); toast.success('Client added — they still need to sign the waiver before any session.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-fg-muted">{rows.length} fitness clients</p>
        <button onClick={()=>setShowCreate(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="fitness-new-client"><Plus className="w-4 h-4"/> New client</button>
      </div>
      <div className="admin-card">
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Goals</th><th>Status</th><th>Waiver</th><th></th></tr></thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.client_id}>
                <td>{c.full_name}</td>
                <td className="text-xs text-fg-muted">{c.email}</td>
                <td className="text-xs">{c.goals || '—'}</td>
                <td>
                  <span className={c.status === 'active' ? 'badge-success' : 'badge-neutral'}>{c.status}</span>
                </td>
                <td className="text-xs">{c.waiver_signed_at ? c.waiver_signed_at.slice(0,10) : <span className="text-[#B83A3A]">Required</span>}</td>
                <td>
                  <Link to={`/admin/fitness/clients/${c.client_id}/assessments`} className="text-brand text-xs inline-flex items-center gap-1" data-testid={`fc-assess-${c.client_id}`}>
                    Assess →
                  </Link>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="6" className="text-center py-6 text-fg-muted">No clients yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showCreate && (
        <Modal onClose={()=>setShowCreate(false)} title="Add fitness client">
          <Field label="Full name"><input className="input" value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} data-testid="fc-name"/></Field>
          <Field label="Email"><input className="input" type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="fc-email"/></Field>
          <Field label="Phone"><input className="input" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/></Field>
          <Field label="Goals"><textarea className="input min-h-[80px]" value={form.goals} onChange={e=>setForm({...form, goals:e.target.value})}/></Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-3">
            <button onClick={()=>setShowCreate(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={create} className="btn-primary text-sm" data-testid="fc-create">Add client</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Trainers ───────────────────────────────────────────────────────────
function TrainersTab() {
  const [rows, setRows] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name:'', bio:'', expertise:[], hourly_rate_individual:120, hourly_rate_group:60, currency:'PLN', active:true });
  const load = () => api.get('/fitness/trainers').then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post('/fitness/trainers', form); setShowCreate(false); setForm({ name:'', bio:'', expertise:[], hourly_rate_individual:120, hourly_rate_group:60, currency:'PLN', active:true }); load(); toast.success('Trainer added.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  const remove = async (t) => {
    if (!await safeConfirm(`Delete trainer ${t.name}?`)) return;
    await api.delete(`/fitness/trainers/${t.trainer_id}`); load();
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-fg-muted">{rows.length} trainers</p>
        <button onClick={()=>setShowCreate(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="fitness-new-trainer"><Plus className="w-4 h-4"/> New trainer</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map(t => (
          <div key={t.trainer_id} className="bento-card">
            <div className="flex justify-between items-start gap-2">
              <div>
                <h3 className="font-heading font-semibold">{t.name}</h3>
                <p className="text-[11px] text-fg-muted">{t.expertise.join(' · ') || 'General trainer'}</p>
              </div>
              <button onClick={()=>remove(t)} className="text-[#B83A3A]" title="Delete"><Trash2 className="w-4 h-4"/></button>
            </div>
            <p className="text-xs text-fg-secondary mt-2 line-clamp-3">{t.bio}</p>
            <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
              <div className="surface border border-soft rounded p-2">
                <div className="text-[10px] text-fg-muted uppercase">Individual</div>
                <div className="font-mono">{t.hourly_rate_individual} {t.currency}</div>
              </div>
              <div className="surface border border-soft rounded p-2">
                <div className="text-[10px] text-fg-muted uppercase">Group</div>
                <div className="font-mono">{t.hourly_rate_group} {t.currency}</div>
              </div>
            </div>
          </div>
        ))}
        {!rows.length && <div className="text-fg-muted text-sm md:col-span-3 text-center py-6">No trainers yet.</div>}
      </div>
      {showCreate && (
        <Modal onClose={()=>setShowCreate(false)} title="Add trainer">
          <Field label="Name"><input className="input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="tr-name"/></Field>
          <Field label="Bio (up to 2000 chars)"><textarea className="input min-h-[160px]" maxLength={2000} value={form.bio} onChange={e=>setForm({...form, bio:e.target.value})} placeholder="Background, certifications, training philosophy…" data-testid="tr-bio"/></Field>
          <Field label="Expertise (comma-separated)"><input className="input" value={form.expertise.join(', ')} onChange={e=>setForm({...form, expertise:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})} placeholder="HIIT, Strength, Pilates, Pregnancy"/></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Individual rate"><input type="number" className="input" value={form.hourly_rate_individual} onChange={e=>setForm({...form, hourly_rate_individual:+e.target.value})}/></Field>
            <Field label="Group rate"><input type="number" className="input" value={form.hourly_rate_group} onChange={e=>setForm({...form, hourly_rate_group:+e.target.value})}/></Field>
            <Field label="Currency"><select className="input" value={form.currency} onChange={e=>setForm({...form, currency:e.target.value})}>{['PLN','EUR','USD','GBP'].map(c=><option key={c}>{c}</option>)}</select></Field>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-3">
            <button onClick={()=>setShowCreate(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={create} className="btn-primary text-sm" data-testid="tr-create">Add trainer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Classes ────────────────────────────────────────────────────────────
function ClassesTab() {
  const [rows, setRows] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name:'', description:'', tier:1, price_tier_1:0, price_tier_2:30, price_tier_3:60, duration_min:60, capacity:12, currency:'PLN', active:true });
  const load = () => api.get('/fitness/classes').then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post('/fitness/classes', form); setShowCreate(false); load(); toast.success('Class added.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  const remove = async (c) => {
    if (!await safeConfirm(`Delete class ${c.name}?`)) return;
    await api.delete(`/fitness/classes/${c.class_id}`); load();
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-fg-muted">{rows.length} classes</p>
        <button onClick={()=>setShowCreate(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="fitness-new-class"><Plus className="w-4 h-4"/> New class</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map(c => (
          <div key={c.class_id} className="bento-card">
            <div className="flex justify-between items-start gap-2">
              <div>
                <h3 className="font-heading font-semibold">{c.name}</h3>
                <p className="text-[11px] text-fg-muted">{c.duration_min} min · cap {c.capacity} · tier-{c.tier}</p>
              </div>
              <button onClick={()=>remove(c)} className="text-[#B83A3A]" title="Delete"><Trash2 className="w-4 h-4"/></button>
            </div>
            <p className="text-xs text-fg-secondary mt-2 line-clamp-3">{c.description}</p>
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div className="surface border border-soft rounded p-2 text-center">
                <div className="text-[10px] text-fg-muted uppercase">Tier 1</div>
                <div className="font-mono">{c.price_tier_1} {c.currency}</div>
              </div>
              <div className="surface border border-soft rounded p-2 text-center">
                <div className="text-[10px] text-fg-muted uppercase">Tier 2</div>
                <div className="font-mono">{c.price_tier_2} {c.currency}</div>
              </div>
              <div className="surface border border-soft rounded p-2 text-center">
                <div className="text-[10px] text-fg-muted uppercase">Tier 3</div>
                <div className="font-mono">{c.price_tier_3} {c.currency}</div>
              </div>
            </div>
          </div>
        ))}
        {!rows.length && <div className="text-fg-muted text-sm md:col-span-3 text-center py-6">No classes yet.</div>}
      </div>
      {showCreate && (
        <Modal onClose={()=>setShowCreate(false)} title="Add class">
          <Field label="Name"><input className="input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="cl-name"/></Field>
          <Field label="Description"><textarea className="input min-h-[80px]" value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Tier"><select className="input" value={form.tier} onChange={e=>setForm({...form, tier:+e.target.value})}>{[1,2,3].map(t=><option key={t} value={t}>Tier {t}</option>)}</select></Field>
            <Field label="Duration (min)"><input type="number" className="input" value={form.duration_min} onChange={e=>setForm({...form, duration_min:+e.target.value})}/></Field>
            <Field label="Capacity"><input type="number" className="input" value={form.capacity} onChange={e=>setForm({...form, capacity:+e.target.value})}/></Field>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Tier-1 price"><input type="number" className="input" value={form.price_tier_1} onChange={e=>setForm({...form, price_tier_1:+e.target.value})}/></Field>
            <Field label="Tier-2 price"><input type="number" className="input" value={form.price_tier_2} onChange={e=>setForm({...form, price_tier_2:+e.target.value})}/></Field>
            <Field label="Tier-3 price"><input type="number" className="input" value={form.price_tier_3} onChange={e=>setForm({...form, price_tier_3:+e.target.value})}/></Field>
            <Field label="Currency"><select className="input" value={form.currency} onChange={e=>setForm({...form, currency:e.target.value})}>{['PLN','EUR','USD','GBP'].map(c=><option key={c}>{c}</option>)}</select></Field>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-3">
            <button onClick={()=>setShowCreate(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={create} className="btn-primary text-sm" data-testid="cl-create">Add class</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Schedule (class instances) ──────────────────────────────────────────
function ScheduleTab() {
  const [instances, setInstances] = useState([]);
  const [classes, setClasses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ class_id:'', trainer_id:'', starts_at:'', capacity:0 });
  const load = () => {
    api.get('/fitness/instances').then(r => setInstances(r.data));
    api.get('/fitness/classes').then(r => setClasses(r.data));
    api.get('/fitness/trainers').then(r => setTrainers(r.data));
  };
  useEffect(() => { load(); }, []);
  const schedule = async () => {
    if (!form.class_id || !form.starts_at) return toast.error('Pick a class + date/time.');
    try {
      await api.post(`/fitness/classes/${form.class_id}/instances`, form);
      setShowAdd(false); load(); toast.success('Scheduled.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-fg-muted">{instances.length} class occurrences</p>
        <button onClick={()=>setShowAdd(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="fitness-schedule-class"><Plus className="w-4 h-4"/> Schedule a class</button>
      </div>
      <div className="admin-card">
        <table>
          <thead><tr><th>When</th><th>Class</th><th>Trainer</th><th>Bookings</th><th>Capacity</th></tr></thead>
          <tbody>
            {instances.map(i => {
              const t = trainers.find(x => x.trainer_id === i.trainer_id);
              return (
                <tr key={i.instance_id}>
                  <td className="text-xs">{new Date(i.starts_at).toLocaleString()}</td>
                  <td>{i.class_name}</td>
                  <td className="text-xs">{t?.name || '—'}</td>
                  <td className="text-xs">{(i.bookings || []).length}</td>
                  <td className="text-xs">{i.capacity}</td>
                </tr>
              );
            })}
            {!instances.length && <tr><td colSpan="5" className="text-center py-6 text-fg-muted">Nothing scheduled.</td></tr>}
          </tbody>
        </table>
      </div>
      {showAdd && (
        <Modal onClose={()=>setShowAdd(false)} title="Schedule class">
          <Field label="Class"><select className="input" value={form.class_id} onChange={e=>setForm({...form, class_id:e.target.value})} data-testid="sched-class"><option value="">Choose…</option>{classes.map(c=><option key={c.class_id} value={c.class_id}>{c.name}</option>)}</select></Field>
          <Field label="Trainer"><select className="input" value={form.trainer_id} onChange={e=>setForm({...form, trainer_id:e.target.value})}><option value="">None</option>{trainers.map(t=><option key={t.trainer_id} value={t.trainer_id}>{t.name}</option>)}</select></Field>
          <Field label="Date & time"><input type="datetime-local" className="input" value={form.starts_at} onChange={e=>setForm({...form, starts_at:e.target.value})} data-testid="sched-when"/></Field>
          <Field label="Capacity (optional override)"><input type="number" className="input" value={form.capacity || ''} onChange={e=>setForm({...form, capacity:+e.target.value})}/></Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-3">
            <button onClick={()=>setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={schedule} className="btn-primary text-sm" data-testid="sched-create">Schedule</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Memberships ────────────────────────────────────────────────────────
function MembershipsTab() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ client_id:'', plan_name:'Standard month', price:250, currency:'PLN', billed_period_days:30, duration_days:30, classes_included:8, max_hold_days:14 });
  const load = () => {
    api.get('/fitness/memberships').then(r => setRows(r.data));
    api.get('/fitness/clients').then(r => setClients(r.data));
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post('/fitness/memberships', form); setShowCreate(false); load(); toast.success('Membership created + invoice generated.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-fg-muted">{rows.length} memberships</p>
        <button onClick={()=>setShowCreate(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="fitness-new-membership"><Plus className="w-4 h-4"/> New membership</button>
      </div>
      <div className="admin-card">
        <table>
          <thead><tr><th>Plan</th><th>Client</th><th>Period</th><th>Price</th><th>Status</th><th>Renewals</th></tr></thead>
          <tbody>
            {rows.map(m => {
              const c = clients.find(x => x.client_id === m.client_id);
              return (
                <tr key={m.membership_id}>
                  <td>{m.plan_name}</td>
                  <td className="text-xs">{c?.full_name || m.client_id}</td>
                  <td className="text-xs">{m.start_date} → {m.end_date}</td>
                  <td className="text-xs font-mono">{m.price} {m.currency}</td>
                  <td><span className={m.status === 'active' ? 'badge-success' : 'badge-neutral'}>{m.status}</span></td>
                  <td className="text-xs">{(m.renewals || []).length}</td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan="6" className="text-center py-6 text-fg-muted">No memberships yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {showCreate && (
        <Modal onClose={()=>setShowCreate(false)} title="Issue membership">
          <Field label="Client"><select className="input" value={form.client_id} onChange={e=>setForm({...form, client_id:e.target.value})} data-testid="mb-client"><option value="">Choose…</option>{clients.map(c=><option key={c.client_id} value={c.client_id}>{c.full_name}</option>)}</select></Field>
          <Field label="Plan name"><input className="input" value={form.plan_name} onChange={e=>setForm({...form, plan_name:e.target.value})}/></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Price"><input type="number" className="input" value={form.price} onChange={e=>setForm({...form, price:+e.target.value})}/></Field>
            <Field label="Currency"><select className="input" value={form.currency} onChange={e=>setForm({...form, currency:e.target.value})}>{['PLN','EUR','USD','GBP'].map(c=><option key={c}>{c}</option>)}</select></Field>
            <Field label="Duration (days)"><input type="number" className="input" value={form.duration_days} onChange={e=>setForm({...form, duration_days:+e.target.value})}/></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Classes included (0=unlimited)"><input type="number" className="input" value={form.classes_included} onChange={e=>setForm({...form, classes_included:+e.target.value})}/></Field>
            <Field label="Max hold (days)"><input type="number" className="input" value={form.max_hold_days} onChange={e=>setForm({...form, max_hold_days:+e.target.value})}/></Field>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-3">
            <button onClick={()=>setShowCreate(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={create} className="btn-primary text-sm" data-testid="mb-create">Issue membership</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Invoices ───────────────────────────────────────────────────────────
function InvoicesTab() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const load = () => {
    api.get('/fitness/invoices').then(r => setRows(r.data));
    api.get('/fitness/clients').then(r => setClients(r.data));
  };
  useEffect(() => { load(); }, []);
  const markPaid = async (inv) => {
    await api.post(`/fitness/invoices/${inv.fitness_invoice_id}/mark-paid`); load(); toast.success('Marked paid.');
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-fg-muted">{rows.length} fitness invoices · <strong>strictly separate from clinical invoices</strong> for compliance.</p>
      </div>
      <div className="admin-card">
        <table>
          <thead><tr><th>Created</th><th>Client</th><th>Type</th><th>Total</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(i => {
              const c = clients.find(x => x.client_id === i.client_id);
              return (
                <tr key={i.fitness_invoice_id}>
                  <td className="text-xs">{(i.created_at || '').slice(0,10)}</td>
                  <td className="text-xs">{c?.full_name || i.client_id}</td>
                  <td className="text-xs">{i.type}</td>
                  <td className="text-xs font-mono">{i.total} {i.currency}</td>
                  <td><span className={i.status === 'paid' ? 'badge-success' : 'badge-neutral'}>{i.status}</span></td>
                  <td>
                    {i.status !== 'paid' && (
                      <button onClick={()=>markPaid(i)} className="text-brand text-xs inline-flex items-center gap-1"><CheckIcon/> Mark paid</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan="6" className="text-center py-6 text-fg-muted">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CheckIcon() { return <span className="text-xs">✓</span>; }

// ─── Shared UI primitives ───────────────────────────────────────────────

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg max-w-xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-heading font-bold">{title}</h3>
          <button onClick={onClose}><X className="w-4 h-4"/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
